<script setup>
import {
  ref,
  reactive,
  defineEmits,
  onMounted,
  nextTick,
  getCurrentInstance,
} from 'vue'
import { focusNextField as focusNextFieldHelper } from '@/helpers'
import { helpers, required } from '@vuelidate/validators'
import { useToast } from 'primevue/usetoast'
import dayjs from 'dayjs'
import errorHandler from '@/helpers/error-handler'
import FxReportContainer from '@/components/FxReportContainer'
import LaporanService from '@/services/LaporanService'
import SupplierService from '@/services/SupplierService'
import useVuelidate from '@vuelidate/core'
import { saveAs } from 'file-saver'

const toast = useToast()
const laporanService = new LaporanService()
const supplierService = new SupplierService()
let dialogViewer = ref(false)
let pdfSource = ref('')
let pdfTitle = ref('PDF Viewer')
let pdfFilename = ref('')

let isLoadingExport = ref(false)
// let isLoadingExportExcel = ref(false)
let form = reactive({
  date_to: new Date(),
  supplier: null,
})
let submitted = ref(false)
let list_supplier = ref([])
let selectedSupplier = ref(null)

const vm = getCurrentInstance().proxy

function focusNextField(refName, selectAll = false) {
  focusNextFieldHelper(vm, refName, selectAll)
}

const rules = {
  date_to: {
    required: helpers.withMessage('Tanggal harus diisi.', required),
  },
}

const v$ = useVuelidate(rules, form)

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['toggle'])

onMounted(async () => {
  const res = await supplierService.get()
  list_supplier.value = res.data

  await nextTick()
  setTimeout(() => {
    focusNextField('supplier')
  }, 100)
})

async function searchSupplier(event) {
  const query = event?.query ?? ''
  try {
    const res = await supplierService.getList(query)
    // keep only suppliers that include query in name
    list_supplier.value = res.data.filter((s) =>
      s.name.toLowerCase().includes(query.toLowerCase())
    )
  } catch (err) {
    errorHandler(err, 'Data Supplier', toast)
  }
}

function onSelectSupplier() {
  form.supplier = selectedSupplier.value?.id || null
  list_supplier.value = []

  nextTick(() => {
    setTimeout(() => {
      focusNextField('date_to')
    }, 100)
  })
}

function onExport(isFormValid) {
  isLoadingExport.value = true
  const endDate = dayjs(form.date_to).format('YYYY-MM-DD')
  const supplierId = form.supplier === null ? 0 : form.supplier

  submitted.value = true
  if (!isFormValid) {
    isLoadingExport.value = false
    return
  }

  laporanService
    .PerubahanHarga(endDate, supplierId)
    .then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)
      pdfSource.value = pdfUrl + '#toolbar=0'
      pdfFilename.value = 'laporan-perubahan-harga.pdf'
      pdfTitle.value = 'PDF Viewer (' + pdfFilename.value + ')'
      dialogViewer.value = true
    })

    .catch((err) => {
      errorHandler(err, 'Laporan Perubahan Harga', toast)
    })
    .finally(() => (isLoadingExport.value = false))
}

function onPrintPdf() {
  const iframe = document.createElement('iframe')
  iframe.src = pdfSource.value
  iframe.style.display = 'none'
  document.body.appendChild(iframe)

  // Use onload to make pdf preview work on firefox
  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
  }
}

function onDownloadPdf() {
  saveAs(pdfSource.value, pdfFilename.value)
}

// function onExportExcel(isFormValid) {
//   isLoadingExportExcel.value = true
//   // const startDate = dayjs(form.date_from).format('YYYY-MM-DD')
//   // const endDate = dayjs(form.date_to).format('YYYY-MM-DD')
//   // const supplierId = form.supplier

//   submitted.value = true

//   if (!isFormValid) {
//     isLoadingExportExcel.value = false
//     return
//   }

//   // laporanService
//   //   .exportLaporanDaftarPembelianExcel(startDate, endDate, supplierId)
//   //   .then((res) => {
//   //     const url = window.URL.createObjectURL(new Blob([res.data]))
//   //     const link = document.createElement('a')
//   //     link.href = url
//   //     link.setAttribute('download', 'laporan-pembelian.xls')
//   //     document.body.appendChild(link)
//   //     link.click()
//   //   })
//   //   .catch((err) => {
//   //     errorHandler(err, 'Laporan Perubahan Harga', this)
//   //   })
//   //   .finally(() => (this.isLoadingExportExcel = false))
// }

function onToggle() {
  emit('toggle')
}

function resetForm() {
  submitted.value = false

  form.date_to = new Date()
  form.supplier = null
  selectedSupplier.value = null

  nextTick(() => {
    setTimeout(() => {
      focusNextField('supplier')
    }, 50)
  })
}
</script>

<template>
  <fx-report-container
    title="Laporan Perubahan Harga"
    :loading="isLoadingExport"
    @toggle="onToggle"
    @export="onExport"
  >
    <template #content>
      <form @submit.prevent="onExport(!v$.$invalid)">
        <div class="formgrid grid">
          <div class="field col-12">
            <div class="font-bold">Filter</div>
          </div>
          <div class="field col-12 md:col-4">
            <AutoComplete
              ref="supplier"
              v-model="selectedSupplier"
              class="w-full"
              input-class="w-full"
              placeholder="Pilih Supplier"
              :suggestions="list_supplier"
              field="name"
              @complete="searchSupplier"
              @item-select="onSelectSupplier"
              @focus="searchSupplier({ query: '' })"
            />
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              ref="date_to"
              v-model="v$.date_to.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_to.$invalid && submitted,
              }"
              placeholder="Tanggal (s/d)"
              class="w-full"
              @date-select="focusNextField('btnCetak')"
            />
            <small
              v-if="
                (v$.date_to.$invalid && submitted) ||
                v$.date_to.$pending.$response
              "
              class="p-error"
              >{{ v$.date_to.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-5">
            <Button
              ref="btnCetak"
              :loading="isLoadingExport"
              type="submit"
              icon="pi pi-file-pdf"
              class="ml-2"
              label="Cetak"
            />
            <!-- <Button
              :loading="isLoadingExportExcel"
              type="button"
              icon="pi pi-file-excel"
              label="Export"
              class="p-button-success ml-2"
              @click="onExportExcel(!v$.$invalid)"
            /> -->
            <Button
              type="reset"
              icon="pi pi-refresh"
              label="Reset"
              class="p-button-outlined ml-2"
              @click="resetForm"
            />
          </div>
        </div>
      </form>
    </template>
  </fx-report-container>
  <Dialog
    :header="pdfTitle"
    v-model:visible="dialogViewer"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '90vw' }"
    :modal="true"
  >
    <div>
      <embed
        :src="pdfSource"
        type="application/pdf"
        width="100%"
        height="600px"
      />
    </div>
    <template #footer>
      <Button
        type="button"
        label="Download"
        icon="pi pi-download"
        class="p-button-text mr-2"
        @click="onDownloadPdf"
      />
      <Button
        type="button"
        label="Print"
        icon="pi pi-print"
        @click="onPrintPdf"
      />
    </template>
  </Dialog>
</template>
